/**
 * Turning the correction screen's codes into words (DECISIONS.md #43).
 *
 * Nothing here holds state and nothing renders, matching `registrationDisplay.ts`.
 *
 * **Where the rule is shared, so is the wording.** The five field rules a correction has in common
 * with a registration — the family name, the length limit, the sex vocabulary and the two
 * birth-date checks — are rendered from `registration:refusal.*`, not from a second set of keys in
 * `correction.json`. Those sentences say nothing about registering; they say what is wrong with a
 * value. A parallel copy would be two sentences for one rule with nothing keeping them equal, which
 * is the drift `AGENTS.md` §7 forbids for documents and is no better for a catalogue.
 *
 * `correction.json` holds what is genuinely this screen's: the heading, the sentence about version
 * 1 staying in the record, and the four refusals a registration cannot produce.
 *
 * **Every code gets a message.** {@link correctionRefusalMessage} is exhaustive over
 * {@link CorrectionRefusalCode} by construction, so a code added on the server and typed into
 * `CORRECTION_REFUSAL_CODES` fails the typecheck here until somebody has written the wording.
 */

import type { TFunction } from 'i18next'

import type { CorrectionRefusalCode, CorrectionRefusalParams } from './correctionApi'
import type { CorrectionDraftProblem, CorrectionField } from './correctionDraft'

/**
 * The lookup the correction screen needs.
 *
 * Five namespaces, and `registration` is one of them for the reason the module comment gives. The
 * other four are `RegistrationTranslate`'s, for the reasons it states: `patient` because the box a
 * value is typed into must carry the word the value is read under, and `auth` for
 * `mayNotDocumentMessage` on the withheld form.
 */
export type CorrectionTranslate = TFunction<
  ['common', 'auth', 'patient', 'registration', 'correction']
>

/**
 * Which of the two acts the screen is being used for.
 *
 * Not a style choice, and the reason one word will not do. In Swedish journalföring a **rättelse**
 * fixes an entry that was wrong, and a **komplettering** adds what was not known; both leave the
 * earlier version readable (HSLF-FS 2016:40), so both are honest about what this screen does. The
 * patient admitted unconscious and identified later is the second: nobody documented anything
 * incorrectly, and calling it a correction asks the clinician to file a colleague's accurate entry
 * as an error.
 *
 * **`uppdatera` is the word this deliberately avoids.** It promises the old value is replaced, which
 * is the one thing `screen.appendOnly` spends a sentence denying — and in a record that is legally
 * authoritative for the outage, a button that promises an update and writes a version is the wrong
 * kind of surprise.
 *
 * `rätta` stays the verb everywhere the act really is a correction: `notes.json` amends a note with
 * it, `registration.json` says a mistake is corrected by a new version, and both are right. What is
 * split here is only this screen, where two acts shared one button.
 */
export type CorrectionMode = 'correct' | 'complete'

/**
 * The mode, read from the record and never from the draft.
 *
 * `isEstimatedIdentity` **as the record currently holds it**: an identity that is still an
 * assumption is one still being established, which is a completion. Reading the draft instead would
 * flip the heading and the Save button under the clinician's hands the moment they cleared the
 * checkbox — halfway through the very act the wording is describing.
 *
 * @param patient the version the form was opened on, not the draft being edited
 */
export function correctionMode(patient: {
  readonly isEstimatedIdentity: boolean
}): CorrectionMode {
  return patient.isEstimatedIdentity ? 'complete' : 'correct'
}

/** The entry point on the patient overview. */
export function correctionOpenLabel(mode: CorrectionMode, t: CorrectionTranslate): string {
  switch (mode) {
    case 'complete':
      return t('correction:screen.openComplete')
    case 'correct':
      return t('correction:screen.openCorrect')
  }
}

/** The form's heading, which must say the same act as the button that opened it. */
export function correctionHeading(mode: CorrectionMode, t: CorrectionTranslate): string {
  switch (mode) {
    case 'complete':
      return t('correction:screen.headingComplete')
    case 'correct':
      return t('correction:screen.headingCorrect')
  }
}

/** The sentence under the heading, saying which of the two cases the form is for. */
export function correctionExplanation(mode: CorrectionMode, t: CorrectionTranslate): string {
  switch (mode) {
    case 'complete':
      return t('correction:screen.explanationComplete')
    case 'correct':
      return t('correction:screen.explanationCorrect')
  }
}

/** The Save button, which names what is about to be written. */
export function correctionSaveLabel(mode: CorrectionMode, t: CorrectionTranslate): string {
  switch (mode) {
    case 'complete':
      return t('correction:screen.saveComplete')
    case 'correct':
      return t('correction:screen.saveCorrect')
  }
}

/**
 * A field's label, reused as the name in a "too long" message.
 *
 * The registration form's labels and the overview's, deliberately unchanged: `Förnamn` is `Förnamn`
 * on both forms, and `Född` is what the header the clinician is correcting calls it.
 */
export function correctionFieldLabel(field: CorrectionField, t: CorrectionTranslate): string {
  switch (field) {
    case 'givenName':
      return t('registration:field.givenName')
    case 'familyName':
      return t('registration:field.familyName')
    case 'birthDate':
      return t('patient:overview.birthDate')
  }
}

/**
 * The sentence under a field, saying what belongs in it.
 *
 * The registration hints, which are about the value rather than about the act: *leave it empty if
 * the name is not known* is as true when correcting as when registering. The one hint that differs
 * is the estimated-identity checkbox's, which is this screen's own — see
 * {@link estimatedIdentityHint}.
 */
export function correctionFieldHint(field: CorrectionField, t: CorrectionTranslate): string {
  switch (field) {
    case 'givenName':
      return t('registration:fieldHint.givenName')
    case 'familyName':
      return t('registration:fieldHint.familyName')
    case 'birthDate':
      return t('registration:fieldHint.birthDate')
  }
}

/**
 * The hint under the estimated-identity checkbox, which is this screen's and not the registration's.
 *
 * The registration's hint explains what marking the box means when nobody can identify the patient.
 * Here the box is usually being *cleared*, and the question of when that is warranted is the open
 * clinical one in #43 — so the hint says who decides rather than pretending there is a rule. It is
 * the one piece of wording on this screen that a clinician review is expected to rewrite.
 */
export function estimatedIdentityHint(t: CorrectionTranslate): string {
  return t('correction:fieldHint.isEstimatedIdentity')
}

/**
 * A field name as it arrived in a `CORRECTION_FIELD_TOO_LONG` param, in words.
 *
 * Falls back to the raw name rather than dropping it, matching `registrationDisplay.ts`: the
 * message's job is to say which box to shorten.
 */
function fieldNameFromParams(field: string | undefined, t: CorrectionTranslate): string {
  switch (field) {
    case 'givenName':
    case 'familyName':
    case 'birthDate':
      return correctionFieldLabel(field, t)
    default:
      return field ?? ''
  }
}

/**
 * What to tell the clinician about a refusal.
 *
 * Every message says, one way or another, that **nothing was written** — which is true of every
 * branch here and is the fact a clinician who has just established who a patient is needs first.
 * The one that says something else is `CORRECTION_VERSION_STALE`, where something *was* written:
 * by somebody else, and the message says so.
 */
export function correctionRefusalMessage(
  code: CorrectionRefusalCode | 'UNKNOWN',
  params: CorrectionRefusalParams,
  t: CorrectionTranslate,
): string {
  switch (code) {
    // ---- The five rules shared with registration, in registration's words. ----

    case 'CORRECTION_FAMILY_NAME_MISSING':
      return t('registration:refusal.familyNameMissing')

    case 'CORRECTION_FIELD_TOO_LONG':
      return t('registration:refusal.fieldTooLong', {
        field: fieldNameFromParams(params.field, t),
        maximum: params.max ?? 0,
      })

    case 'CORRECTION_SEX_UNKNOWN':
      return t('registration:refusal.sexUnknown')

    case 'CORRECTION_BIRTH_DATE_MALFORMED':
      return t('registration:refusal.birthDateMalformed')

    case 'CORRECTION_BIRTH_DATE_IN_FUTURE':
      return t('registration:refusal.birthDateInFuture')

    // ---- The four a registration cannot produce. ----

    case 'CORRECTION_VERSION_STALE':
      // Somebody else corrected this patient while the form was open, and with two clinicians
      // reading one unidentified patient that is the likely case rather than the rare one. The
      // message says what happened and what the screen has done about it — reloaded the identity,
      // with what was typed still in the boxes. `params.current` is deliberately not shown: it is
      // a uuid, and it may already have moved again.
      return t('correction:refusal.versionStale')

    case 'CORRECTION_NO_CHANGE':
      // Not an error in the record's eyes and the wording does not treat it as one. Version N+1
      // identical to version N would carry an `amend` row asserting a correction that corrected
      // nothing, so the refusal is the record staying honest.
      return t('correction:refusal.noChange')

    case 'PATIENT_NOT_FOUND':
      // Reachable with the form open: the patient was read, then retracted, then this. Also what a
      // guessed id lands on — the server answers the same for both, deliberately.
      return t('correction:refusal.patientNotFound')

    case 'PATIENT_RETRACTED':
      // Withdrawn from the record as `entered-in-error`, which is as close to deleted as this
      // schema gets. Correcting one would put it back on every screen, so the retraction has the
      // last word and the message says where to go instead.
      return t('correction:refusal.patientRetracted')

    case 'ROLE_MAY_NOT_DOCUMENT':
      // Reaching this means the form was offered and the write was refused anyway — the same
      // argument `registrationDisplay.ts` makes for not rendering the role from `params.roleCode`.
      return t('correction:refusal.roleMayNotDocument')

    case 'CORRECTION_SUPERSEDES_MISSING':
      // Unreachable from this form, which always sends the `versionId` it was opened with. Worded
      // as a reload rather than as a field to fix, because there is no field for it: if it ever
      // arrives, the screen's state is what is wrong.
      return t('correction:refusal.supersedesMissing')

    case 'CORRECTION_BODY_MISSING':
    case 'UNKNOWN':
      return t('correction:refusal.unknown')
  }
}

/**
 * What to tell the clinician when the correction could not be attempted.
 *
 * Separate wording from every refusal, because the action is opposite: a refusal is something to
 * fix on the form, and this is something to wait out — so the message says to leave the form open.
 */
export function correctionUnavailableMessage(t: CorrectionTranslate): string {
  return t('correction:refusal.unavailable')
}

/** The same, for a problem this side found before sending. */
export function correctionProblemMessage(
  problem: CorrectionDraftProblem,
  t: CorrectionTranslate,
): string {
  // One function for both, deliberately: the client's checks mirror the server's, so a mistake
  // reads the same whether the form caught it or the box did.
  return correctionRefusalMessage(problem.code, problem.params, t)
}
